import React, { useState, useEffect } from "react";
import { Markdown } from "../../lib/markdown";
import { Button } from "../atoms/button";
import { Badge } from "../atoms/badge";
import { Eyebrow } from "../atoms/eyebrow";
import { DisplayHeading } from "../atoms/display-heading";
import type {
	Walkthrough,
	WalkthroughStepType,
} from "../../hooks/useWalkthrough";

interface WalkthroughPanelProps {
	walkthrough: Walkthrough;
	stepIndex: number;
	onNext: () => void;
	onPrev: () => void;
	onGoTo: (index: number) => void;
	onLoadStepCode: () => void;
	onRun: () => void;
	onClose: () => void;
	isReady: boolean;
}

const STEP_TONE: Record<WalkthroughStepType, "neutral" | "accent" | "success" | "info"> = {
	intro: "neutral",
	concept: "info",
	try: "accent",
	recap: "success",
};

const STEP_LABEL: Record<WalkthroughStepType, string> = {
	intro: "Intro",
	concept: "Concept",
	try: "Your Turn",
	recap: "Recap",
};

const WalkthroughPanel: React.FC<WalkthroughPanelProps> = ({
	walkthrough,
	stepIndex,
	onNext,
	onPrev,
	onGoTo,
	onLoadStepCode,
	onRun,
	onClose,
	isReady,
}) => {
	const step = walkthrough.steps[stepIndex];
	const total = walkthrough.steps.length;
	const isFirst = stepIndex === 0;
	const isLast = stepIndex === total - 1;
	const [hintOpen, setHintOpen] = useState(false);

	// Collapse hint when we navigate to a new step.
	useEffect(() => {
		setHintOpen(false);
	}, [stepIndex]);

	if (!step) return null;

	return (
		<aside className="flex flex-col h-full bg-white dark:bg-[#0c0c0e] border-l border-gray-200/80 dark:border-white/[0.06]">
			{/* Header */}
			<header className="flex-shrink-0 px-4 pt-3.5 pb-3 border-b border-gray-200/80 dark:border-white/[0.06]">
				<div className="flex items-start justify-between gap-2 mb-1.5">
					<div className="min-w-0">
						<Eyebrow className="!text-[9px] !tracking-[0.18em] mb-1">
							Walkthrough · {walkthrough.estimatedTime}
						</Eyebrow>
						<DisplayHeading size="md" className="truncate">
							{walkthrough.title}
						</DisplayHeading>
					</div>
					<button
						onClick={onClose}
						className="flex-shrink-0 -mt-0.5 -mr-1 w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
						title="Close walkthrough"
						aria-label="Close walkthrough"
					>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Progress dots */}
				<div className="flex items-center gap-1.5 mt-2.5">
					{walkthrough.steps.map((s, i) => {
						const isActive = i === stepIndex;
						const isDone = i < stepIndex;
						return (
							<button
								key={i}
								onClick={() => onGoTo(i)}
								title={`${STEP_LABEL[s.type]}: ${s.title}`}
								aria-label={`Go to step ${i + 1}: ${s.title}`}
								className={`h-1 rounded-full transition-all ${
									isActive
										? "w-6 bg-orange-500 dark:bg-amber-400"
										: isDone
											? "w-3 bg-orange-300 dark:bg-amber-500/40 hover:bg-orange-400"
											: "w-3 bg-gray-200 dark:bg-white/[0.08] hover:bg-gray-300 dark:hover:bg-white/[0.12]"
								}`}
							/>
						);
					})}
					<span className="ml-auto text-[10px] tabular text-gray-400 dark:text-gray-600">
						{stepIndex + 1} / {total}
					</span>
				</div>
			</header>

			{/* Body */}
			<div className="flex-1 overflow-y-auto px-4 py-4">
				<div className="flex items-center gap-2 mb-2">
					<Badge tone={STEP_TONE[step.type]} className="normal-case !text-[10px] !tracking-tight">
						{STEP_LABEL[step.type]}
					</Badge>
					<h2 className="text-[15px] font-semibold tracking-tight text-gray-900 dark:text-gray-100 leading-tight">
						{step.title}
					</h2>
				</div>

				<Markdown source={step.body} />

				{/* Code action */}
				{step.code && (
					<div className="mt-3 p-2.5 rounded-md border border-dashed border-gray-300 dark:border-white/[0.08] bg-gray-50/60 dark:bg-white/[0.02]">
						<div className="flex items-center justify-between gap-2">
							<div className="min-w-0">
								<Eyebrow className="!text-[9px] !tracking-[0.16em]">
									{step.type === "try" ? "Starter code" : "Example"}
								</Eyebrow>
								<p className="text-[11.5px] text-gray-500 dark:text-gray-500 mt-0.5">
									{step.type === "try"
										? "Load it into the editor and try it yourself."
										: "Load it into the editor and run."}
								</p>
							</div>
							<div className="flex items-center gap-1.5 flex-shrink-0">
								<Button variant="secondary" size="xs" onClick={onLoadStepCode}>
									Load
								</Button>
								<Button
									variant="success"
									size="xs"
									onClick={() => {
										onLoadStepCode();
										// Defer run so the new code is in state before execution
										setTimeout(onRun, 0);
									}}
									disabled={!isReady}
								>
									Load &amp; Run
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Hint */}
				{step.hint && (
					<div className="mt-3">
						<button
							onClick={() => setHintOpen((v) => !v)}
							className="flex items-center gap-1.5 text-[11.5px] font-medium text-orange-700 dark:text-amber-300 hover:underline"
						>
							<svg
								className={`w-3 h-3 transition-transform ${hintOpen ? "rotate-90" : ""}`}
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
							</svg>
							{hintOpen ? "Hide hint" : "Stuck? Show hint"}
						</button>
						{hintOpen && (
							<div className="mt-1.5 p-2.5 rounded-md bg-amber-50/80 dark:bg-amber-500/[0.07] border border-amber-200 dark:border-amber-500/20">
								<Markdown source={step.hint} />
							</div>
						)}
					</div>
				)}
			</div>

			{/* Footer / Nav */}
			<footer className="flex-shrink-0 px-3 py-2.5 border-t border-gray-200/80 dark:border-white/[0.06] flex items-center justify-between gap-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={onPrev}
					disabled={isFirst}
				>
					<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
					</svg>
					Back
				</Button>

				{isLast ? (
					<Button variant="primary" size="sm" onClick={onClose}>
						Finish
					</Button>
				) : (
					<Button variant="primary" size="sm" onClick={onNext}>
						Next
						<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
						</svg>
					</Button>
				)}
			</footer>
		</aside>
	);
};

export default WalkthroughPanel;
